<template>
  <div class="bg-gray-50 rounded-xl overflow-hidden">
    <div class="relative bg-gray-900" style="height: 500px;">
      <img
        v-if="imageData"
        ref="imageRef"
        :src="imageData"
        class="max-w-full block"
        style="max-height: 500px; width: 100%; object-fit: contain;"
        alt="Image to crop"
        @load="handleImageLoad"
      >
      <Uploader v-else @upload="handleUpload" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import Cropper from 'cropperjs'
import Uploader from './Uploader.vue'

const props = defineProps({
  imageData: {
    type: String,
    default: null
  },
  aspectRatio: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['ready', 'upload'])

const handleUpload = (data) => {
  emit('upload', data)
}

const imageRef = ref(null)
let cropper = null
let isInitialized = false

const initCropper = () => {
  if (cropper) {
    cropper.destroy()
    cropper = null
  }
  
  if (!imageRef.value) {
    console.log('imageRef not ready')
    return
  }
  
  try {
  cropper = new Cropper(imageRef.value, {
    aspectRatio: props.aspectRatio,
    viewMode: 1,
    dragMode: 'crop',
    autoCropArea: 0.8,
    restore: false,
    guides: true,
    center: true,
    highlight: true,
    cropBoxMovable: true,
    cropBoxResizable: true,
    toggleDragModeOnDblclick: false,
    movable: true,
    zoomable: true,
    background: false,
    modal: true,
    responsive: true,
    checkCrossOrigin: false,
    ready() {
      console.log('Cropper ready')
      emit('ready')
    }
  })
    isInitialized = true
  } catch (error) {
    console.error('Failed to initialize cropper:', error)
  }
}

const handleImageLoad = () => {
  console.log('Image loaded')
  if (!isInitialized || !cropper) {
    initCropper()
  }
}

watch(() => props.aspectRatio, (newRatio) => {
  if (cropper) {
    cropper.setAspectRatio(newRatio)
  }
})

const exportImage = (options) => {
  if (!cropper) {
    console.error('Cropper not initialized')
    return
  }
  
  console.log('getCroppedCanvas options:', options)
  
  const canvasOptions = {
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high'
  }
  
  if (options.width && options.height) {
    canvasOptions.width = options.width
    canvasOptions.height = options.height
  }
  
  console.log('Final canvasOptions:', canvasOptions)
  
  const canvas = cropper.getCroppedCanvas(canvasOptions)
  
  console.log('Canvas size:', canvas?.width, 'x', canvas?.height)
  
  if (!canvas) {
    alert('Failed to generate cropped image')
    return
  }
  
  const mimeType = options.format === 'png' ? 'image/png' : 'image/jpeg'
  const quality = options.quality / 100
  
  canvas.toBlob((blob) => {
    if (!blob) return
    
    console.log('Blob size:', blob.size, 'bytes')
    
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = `cropped-${options.filename}.${options.format}`
    link.href = url
    link.click()
    
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 100)
  }, mimeType, quality)
}

onBeforeUnmount(() => {
  if (cropper) {
    cropper.destroy()
  }
})

defineExpose({
  export: exportImage
})
</script>
